import React from 'react';

import { styled } from '../theme';
import { Color } from '../utility/common-props-helpers';
import Loading from './Loading';
import SvgIcon from './SvgIcon';
import Text from './Text';

/**
 * Types
 */

type Props = {
    children: React.ReactNode;
    className?: string;
    disabled?: boolean;
    loading?: boolean;
    style?: React.CSSProperties;
    type?: 'button' | 'submit' | 'reset';
    color?: Color;
    beforeIconSrc?: string;
    isTextButton?: boolean;
    onClick?: (ev: any) => void;
};

/**
 * Styled
 */

const BaseButton = styled(Text.withComponent('button'))<Props>(({ theme }) => ({
    minHeight: 50,
    height: 50,
    minWidth: 200,
    fontSize: '1.6 rem',
    padding: '6px 20px',
    cursor: 'pointer',
    borderRadius: theme.borderRadius.base,
    display: 'inline-flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    position: 'relative',
}));

const TextButton = styled(BaseButton)<Props>(({ theme, disabled, color }) => ({
    color: theme.color[`${color}Text`],
    background: theme.color[`${color}1`],
    border: 'none',
    ':hover': {
        color: theme.color[`${color}2`],
    },
    ':active, &.active': {
        color: theme.color[`${color}3`],
    },

    ...(disabled && {
        cursor: 'default',
        pointerEvents: 'none',
        color: theme.color.disabled,
        background: theme.color[`${color}1`],
    }),
}));

const ColoredButton = styled(BaseButton)<Props>(({ theme, disabled, color }) => ({
    color: theme.color[`${color}Text`],
    background: theme.color[`${color}1`],
    border: theme.border[`${color}`] || 'unset',
    boxShadow: theme.boxShadow[`${color}1`],
    ':hover': {
        background: theme.color[`${color}2`],
        boxShadow: theme.boxShadow[`${color}2`],
    },
    ':active, &.active': {
        background: theme.color[`${color}3`],
        boxShadow: theme.boxShadow[`${color}3`],
    },

    ...(disabled && {
        cursor: 'default',
        pointerEvents: 'none',
        background: theme.color.disabled,
        boxShadow: 'unset',
    }),
}));

const OutlinedButton = styled(BaseButton)<Props>(({ theme, disabled, color }) => ({
    color: theme.color[`${color}Text`],
    background: theme.color[`${color}1`],
    border: theme.border[`${color}`] || 'unset',
    ':hover': {
        background: theme.color[`${color}2`],
        color: theme.color[`${color}1`],

        svg: {
            color: theme.color.white,
        },
    },
    ':active, &.active': {
        background: theme.color[`${color}3`],
        color: theme.color[`${color}1`],
    },
    ...(disabled && {
        cursor: 'default',
        pointerEvents: 'none',
        background: theme.color.white,
        color: theme.color.disabled,
        borderColor: theme.color.disabled,
    }),
}));

const StyledIcon = styled(SvgIcon)({
    marginRight: 10,
});

/**
 * Component
 */

const Button = React.forwardRef(
    (
        {
            beforeIconSrc,
            children,
            color = 'base',
            type = 'button',
            isTextButton = false,
            loading = false,
            ...props
        }: Props,
        ref
    ) => {
        const StyledButton = isTextButton
            ? TextButton
            : color === 'base'
              ? OutlinedButton
              : ColoredButton;

        if (loading) {
            return (
                <StyledButton color={color} ref={ref} type={type} {...props}>
                    <Loading size="small" centered />
                </StyledButton>
            );
        }

        return (
            <StyledButton color={color} ref={ref} type={type} {...props}>
                {beforeIconSrc && <StyledIcon alt="icon" src={beforeIconSrc} />}
                {children}
            </StyledButton>
        );
    }
);

export default Button;
